import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { WeeklyItem, OccurrenceEditPayload } from '@/types/schedule';

export function useTeacherWeeklySchedule(teacherCode: string | undefined, weekStartISO: string) {
  const qc = useQueryClient();
  const key = ['weekly-schedule', teacherCode, weekStartISO];

  const scheduleQuery = useQuery({
    queryKey: key,
    enabled: Boolean(teacherCode && weekStartISO),
    staleTime: 2 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_teacher_weekly_schedule', {
        _teacher_code: teacherCode,
        _week_start: weekStartISO,
      });
      if (error) throw error;
      return (data as unknown) as { week_start: string; week_end: string; schedule: WeeklyItem[] };
    },
    placeholderData: (prev) => prev,
  });

  const confirmMutation = useMutation({
    mutationFn: async (occurrenceId: string) => {
      const { data, error } = await supabase.rpc('confirm_occurrence', {
        _occurrence_id: occurrenceId,
        _teacher_code: teacherCode,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const requestEditMutation = useMutation({
    mutationFn: async (params: { occurrenceId: string; payload: OccurrenceEditPayload }) => {
      const { data, error } = await supabase.rpc('request_occurrence_edit', {
        _occurrence_id: params.occurrenceId,
        _teacher_code: teacherCode,
        _payload: params.payload,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const getBalances = async (occurrenceId: string) => {
    const { data, error } = await supabase.rpc('get_occurrence_student_balances', {
      _occurrence_id: occurrenceId,
    });
    if (error) throw error;
    return data as { student_id: string; remaining_minutes: number; held_minutes: number }[];
  };

  return { scheduleQuery, confirmMutation, requestEditMutation, getBalances };
}
